import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Computed once at module load; window is available at this point in CRA.
const IS_OAUTH_CALLBACK =
  typeof window !== "undefined" && window.location.hash?.includes("session_id=");

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Skip the loading spinner when we're returning from OAuth — AuthCallback handles auth.
  const [loading, setLoading] = useState(!IS_OAUTH_CALLBACK);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      if (e?.response && e.response.status !== 401) {
        console.error("AuthContext.checkAuth failed", e);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (IS_OAUTH_CALLBACK) return undefined;
    const id = setTimeout(checkAuth, 0);
    return () => clearTimeout(id);
  }, [checkAuth]);

  const loginWithPassword = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, name, password) => {
    const { data } = await api.post("/auth/register", { email, name, password });
    setUser(data.user);
    return data.user;
  }, []);

  const completeGoogleLogin = useCallback(async (session_id) => {
    const { data } = await api.post("/auth/google-session", { session_id });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("AuthContext.logout failed", e);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginWithPassword, register, completeGoogleLogin, logout, refresh: checkAuth }),
    [user, loading, loginWithPassword, register, completeGoogleLogin, logout, checkAuth],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
